<template>
  <span v-if="canLookup">
    <v-btn v-if="showTooltip" v-tip="tooltipText" :aria-label="tooltipText" :loading="loading" icon @click="open">
      <v-icon>mdi-room-service</v-icon>
    </v-btn>
    <template v-else>
      <v-btn :loading="loading" class="mt-2" small text @click="open">
        <v-icon class="mr-2" small>mdi-page-next-outline</v-icon>
        Liste öffnen
      </v-btn>
    </template>

    <!-- DIALOGS -->
    <to-dos-widget-institution-link-dialog v-if="activeInstitution && activeInstitution.name" v-model="showLinkDialog"
                                           :acquisition-lists="activeAcquisitionLists" :institution="activeInstitution"/>
  </span>
</template>
<script lang="ts">
import Vue from 'vue';
import mongoose from 'mongoose';
import {NotificationLike} from '@/types/NotificationLike.type';
import {ToDoAssignedNotificationDocument} from '@/documentTypes/notifier/discriminators/ToDoAssignedNotification.document';
import {AcquisitionList} from '@/documentTypes/institutioner/acquisition/AcquisitionList.document';
import {Institution} from '@/interfaces/institution.interface';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {AcquisitionListFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/AcquisitionListFields';
import {InstitutionFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/InstitutionFields';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import ToDosWidgetInstitutionLinkDialog from '@/components/dashboard/widgets/toDos/toDosWidgetInstitutionLinkDialog.vue';

const SILENT = {handledStatuses: [403, 404]};

/** Opens the active acquisition lists of the institution of a to-do notification; loads them only on click */
export default Vue.extend({
  components: {ToDosWidgetInstitutionLinkDialog},
  props: {
    notification: {
      type: Object as () => NotificationLike<ToDoAssignedNotificationDocument>,
      required: true,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      default: 'Akquiseliste öffnen',
    },
  },
  data: () => ({
    loading: false,
    showLinkDialog: false,
    activeAcquisitionLists: [] as AcquisitionList[],
    activeInstitution: null as null | Institution,
  }),
  computed: {
    institutionId(): mongoose.Types.ObjectId | undefined {
      const payload = this.notification.messagePayload;
      return payload?.entityType === CrudEntityTypes.INSTITUTION && payload.entityId ? payload.entityId : undefined;
    },
    canLookup(): boolean {
      const manager = this.$$crudAccessManager;
      return !!this.institutionId
        && manager.canReadAllProvided(CrudEntityTypes.ACQUISITION_LIST, AcquisitionListFields.NAME, AcquisitionListFields.INSTITUTIONS)
        && manager.entity(CrudEntityTypes.INSTITUTION, this.institutionId).canRead(InstitutionFields.NAME);
    },
  },
  methods: {
    async open() {
      const id = this.institutionId;
      if (!id || this.loading) {
        return;
      }
      this.loading = true;
      try {
        const resp = await InstitutionerApi.acquisition.find({
          fields: [AcquisitionListFields.NAME, AcquisitionListFields.FOR_EMPLOYEE, AcquisitionListFields.ASSIGNEE, AcquisitionListFields.INSTITUTIONS],
          filter: {
            institution: [id],
            isActive: true,
          },
          skipPagination: true,
        }, SILENT);
        if (!resp.acquisitionLists.length) {
          this.$$showSnackbar('Der Standort ist in keiner aktiven Akquiseliste', 'info');
          return;
        }
        this.activeAcquisitionLists = resp.acquisitionLists;
        this.activeInstitution = await InstitutionerApi.findById(id, {fields: [InstitutionFields.NAME]} as any, SILENT);
        this.showLinkDialog = !!this.activeInstitution?.name;
      } catch (e) {
        this.$$showSnackbar('Die Akquiselisten konnten nicht geladen werden', 'warning');
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
